"use client";

import { useMemo } from "react";
import { Button } from "./ui/button";

import { useNavigate } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "./ui/card";
import { ArrowRight, MoreVertical } from "lucide-react";
import { Skeleton } from "./ui/skeleton";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "./ui/dropdown-menu";
import {
  useWalletAssets,
  useFeatureFlagsByCategory,
  useMarketAssets,
} from "../hooks/query/queries";
import carouselItem from "../assets/images/autostack/carousel_item_1.svg";
import { useAuth } from "../context/AuthContext";
import { useCurrencySelection } from "../context/CurrencySelectionContext";
import { formatCurrency } from "../utils/utils";
import { cn } from "../lib/utils";
import CurrencyFormatter from "../services/currencyFormatterService";

export default function CryptoAssetComponentDashboard({
  styleClasses,
  fromDashboard = true,
}) {
  const { data: walletAssetsData, isLoading: isLoadingAssets } =
    useWalletAssets();
  const { data: featureFlags } = useFeatureFlagsByCategory("mainFeatures");
  const { data: marketAssets } = useMarketAssets();
  const assetsForUse = useMemo(
    () => walletAssetsData?.assets || [],
    [walletAssetsData?.assets]
  );
  const { settings } = useAuth();
  const { selectedCurrency, isInProvider } = useCurrencySelection();
  const navigate = useNavigate();

  const currencyFormatter = useMemo(
    () =>
      new CurrencyFormatter({
        locale: "en-US",
        decimalPlaces: 2,
        cryptocurrencyDecimalPlaces: 2,
        stablecoins: ["USDT", "USDC", "DAI"],
      }),
    []
  );

  const zarToUsdtRate = useMemo(() => {
    const zarEstimate = parseFloat(walletAssetsData?.zarEstimate) || 0;
    const usdtEstimate = parseFloat(walletAssetsData?.usdtEstimate) || 0;
    if (zarEstimate > 0 && usdtEstimate > 0) {
      return usdtEstimate / zarEstimate;
    }
    return 0;
  }, [walletAssetsData?.zarEstimate, walletAssetsData?.usdtEstimate]);

  const get24hChange = (asset) => {
    if (!marketAssets || !asset) return null;

    const assetSymbol =
      asset.asset?.toUpperCase() ||
      asset.symbol?.toUpperCase() ||
      (asset.info?.asset && asset.info.asset.toUpperCase());

    if (!assetSymbol) return null;

    let marketData = null;
    if (Object.prototype.hasOwnProperty.call(marketAssets, assetSymbol)) {
      const safeAccess = Object.getOwnPropertyDescriptor(
        marketAssets,
        assetSymbol
      );
      if (safeAccess) {
        marketData = safeAccess.value;
      }
    }

    if (!marketData) {
      marketData = Object.values(marketAssets).find(
        (data) =>
          data.symbol?.toUpperCase() === assetSymbol ||
          data.info?.asset?.toUpperCase() === assetSymbol
      );
    }

    return marketData?.percent_change_24h ?? null;
  };

  const handleNavigate = () => {
    navigate("/trading/digitalWallet");
    window.scrollTo(0, 0);
  };

  const handleOpenAssetDashboard = (asset) => {
    navigate("/trading/asset-dashboard", {
      state: asset,
    });
    window.scrollTo(0, 0);
  };

  const handleTransaction = (asset, type) => {
    let selectedTab = null;
    let parentTab = null;
    let tabActive = null;

    switch (type) {
      case "a":
        selectedTab = "Buy";
        parentTab = "buy-sell";
        tabActive = "Buy";
        break;
      case "b":
        selectedTab = "Sell";
        parentTab = "buy-sell";
        tabActive = "Sell";
        break;
      case "c":
        parentTab = "send-receive";
        tabActive = "send";
        break;
      case "d":
        parentTab = "send-receive";
        tabActive = "receive";
        break;
      default:
        return;
    }

    const isZar =
      asset?.asset?.toLowerCase() === "zar" ||
      asset?.assetName === "South African Rand";

    const transformedAsset = asset
      ? {
          ...asset,
          assetId: asset.assetId || asset.id || asset._id || asset.asset,
          assetIcon: asset.assetIcon || asset.image,
          asset: asset.asset,
          assetName: asset.assetName,
          currencyType: asset.currencyType || asset.type,
          type: asset.type || asset.currencyType,
          availableAmount: asset.availableAmount,
          availableAmountFormatted:
            asset.forattedAvailableAmount || asset.availableAmountFormatted,
          valueInZar: asset.valueInZar,
          valueInUsdt: asset.valueInUsdt,
        }
      : null;

    const shouldPassAsset = !isZar || (isZar && type !== "a");

    navigate("/trading/digitalWallet", {
      state: {
        ...(shouldPassAsset && transformedAsset
          ? {
              selectedCrypto: {
                ...transformedAsset,
                _selectionTimestamp: Date.now(),
              },
            }
          : {}),
        type: parentTab,
        selectedTab,
        tabActive,
      },
    });
  };

  const zarAsset = assetsForUse?.find(
    (asset) =>
      asset.asset?.toLowerCase() === "zar" ||
      asset.assetName === "South African Rand"
  );

  const otherAssets = (() => {
    const filtered = assetsForUse?.filter((asset) => {
      const isZar =
        asset.asset?.toLowerCase() === "zar" ||
        asset.assetName === "South African Rand";
      if (settings?.digitalWallet?.hideZeroBalances === true) {
        const availableAmount = parseFloat(asset.availableAmount) || 0;
        return !isZar && availableAmount > 0;
      }
      return !isZar;
    });
    const sorted = filtered?.sort((a, b) => {
      const priceA = a.valueInZar || 0;
      const priceB = b.valueInZar || 0;
      return priceB - priceA;
    });
    // return fromDashboard ? sorted?.slice(0, 4) : sorted;
    return sorted;
  })();

  return (
    <Card className={cn("flex flex-col w-full", styleClasses)}>
      <CardHeader className="pb-2">
        <CardTitle className="text-[20px] font-semibold text-[#6D6584]">
          Assets
        </CardTitle>
      </CardHeader>

      <CardContent
        className={`p-0 flex flex-col flex-1 overflow-hidden ${fromDashboard ? "min-h-[525px] max-h-[525px]" : ""}`}
      >
        <div className="space-y-6 p-6 pt-4 flex-1 overflow-y-auto min-h-0">
          {isLoadingAssets ? (
            <div className="space-y-6">
              <div className="space-y-3">
                <Skeleton className="h-5 w-32 bg-gray-300" />
                <div className="grid grid-cols-2 gap-4">
                  <Skeleton className="h-16 bg-gray-300" />
                </div>
              </div>
              <div className="space-y-3">
                <Skeleton className="h-5 w-32 bg-gray-300" />
                <div className="space-y-3">
                  {[...Array(3)].map((_, idx) => (
                    <Skeleton key={idx} className="h-16 bg-gray-300" />
                  ))}
                </div>
              </div>
            </div>
          ) : assetsForUse?.length > 0 ? (
            <>
              {zarAsset && (
                <div className="space-y-3">
                  <div className="flex text-[14px] font-semibold text-[#6D6584] pb-2">
                    <div
                      className={`${fromDashboard ? "w-[35%]" : "md:w-[31%] w-[35%]"}`}
                    >
                      Local currency
                    </div>
                    <div
                      className={`${fromDashboard ? "w-[35%]" : "md:w-[30%] w-[35%] hidden md:block"}`}
                    >
                      Balance
                    </div>
                  </div>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3 w-[35%]">
                      <img
                        src={zarAsset.image}
                        alt={`${zarAsset.assetName} icon`}
                        className="w-8 h-8"
                      />
                      <div className="font-medium text-[#423E51]">
                        {zarAsset.asset.toUpperCase()}
                      </div>
                    </div>
                    <div
                      className={`font-semibold text-[#423E51] w-[65%] ${fromDashboard ? "text-left" : "md:text-left text-right"}`}
                    >
                      {formatCurrency(zarAsset.availableAmount || 0, "R")}
                    </div>
                    {!fromDashboard && (
                      <div className="md:flex hidden items-center justify-end gap-2 w-[14%]">
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button
                              variant="outline"
                              size="icon"
                              className="rounded-full z-[11] h-8 w-8"
                              onClick={(e) => e.stopPropagation()}
                            >
                              <MoreVertical className="w-4 h-4 text-[#6D6584]" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent
                            sideOffset={8}
                            className="min-w-[11rem] rounded-lg p-3"
                          >
                            {featureFlags?.buyCrypto && (
                              <DropdownMenuItem asChild>
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleTransaction(zarAsset, "a");
                                  }}
                                  className="flex justify-between items-center w-full gap-3 sm:gap-14 px-3 py-3 sm:py-5 text-sm hover:bg-gray-100"
                                >
                                  <span>Buy</span>
                                  <div className="flex justify-center items-center border bg-white h-8 w-8 sm:h-10 sm:w-10 rounded-full hover:bg-gray-100">
                                    <ArrowRight className="w-4 h-4 sm:w-5 sm:h-5 text-gray-500" />
                                  </div>
                                </button>
                              </DropdownMenuItem>
                            )}
                          </DropdownMenuContent>
                        </DropdownMenu>
                        <button
                          onClick={() => handleOpenAssetDashboard(zarAsset)}
                          className="w-8 h-8 rounded-full border border-[#EBE9EF] flex items-center justify-center hover:bg-gray-50 transition-colors"
                        >
                          <ArrowRight className="w-4 h-4 text-[#6D6584]" />
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {otherAssets && otherAssets.length > 0 && (
                <div className="space-y-3">
                  <div
                    className={`flex items-center pb-2 ${fromDashboard ? "justify-between gap-4" : "md:justify-between justify-start gap-2 md:gap-4"}`}
                  >
                    <div
                      className={`text-[14px] font-semibold text-[#6D6584] ${fromDashboard ? "w-[35%]" : "md:w-[30%] w-[35%]"}`}
                    >
                      Your crypto
                    </div>
                    <div
                      className={`text-[14px] font-semibold text-[#6D6584] ${fromDashboard ? "w-[35%]" : "md:w-[30%] w-[35%]"}`}
                    >
                      Balance
                    </div>
                    <div
                      className={`text-[14px] font-semibold text-[#6D6584] ${fromDashboard ? "w-[30%]" : "md:w-[40%] w-[30%] hidden md:block"}`}
                    >
                      Price
                    </div>
                  </div>
                  <div className="space-y-4">
                    {otherAssets.map((asset) => {
                      const availableAmount =
                        parseFloat(asset.availableAmount) || 0;
                      let valueInZar = parseFloat(asset.valueInZar) || 0;

                      if (isNaN(valueInZar)) valueInZar = 0;

                      const valueInUsdt =
                        zarToUsdtRate > 0 ? valueInZar * zarToUsdtRate : 0;

                      const estimatedPrice = asset.assetEstimatedPrice;

                      const effectiveCurrency =
                        isInProvider && selectedCurrency === "USDT"
                          ? "USDT"
                          : "ZAR";

                      const balanceValue =
                        effectiveCurrency === "USDT" ? valueInUsdt : valueInZar;

                      const formattedBalance =
                        effectiveCurrency === "USDT"
                          ? currencyFormatter.format(
                              balanceValue,
                              "crypto",
                              "USDT",
                              { showSymbol: true }
                            )
                          : currencyFormatter.format(
                              balanceValue,
                              "fiat",
                              "ZAR"
                            );

                      const change24h = get24hChange(asset);
                      const has24hChange =
                        change24h !== null && change24h !== undefined;
                      const isPositiveChange = has24hChange && change24h >= 0;
                      const formatted24hChange = has24hChange
                        ? `${isPositiveChange ? "+" : "-"}${Math.abs(change24h).toFixed(2)}%`
                        : null;

                      return (
                        <div
                          key={asset.id}
                          className={`flex ${fromDashboard ? "justify-between gap-4" : "md:justify-between justify-start gap-2 md:gap-4"} items-center`}
                        >
                          <div className="flex items-center gap-3 w-[35%]">
                            <img
                              src={asset.image}
                              alt={`${asset.assetName} icon`}
                              className="w-10 h-10"
                            />
                            <div>
                              <div className="font-semibold text-[#423E51]">
                                {asset.asset.toUpperCase()}
                              </div>
                              <div className="text-sm text-gray-500">
                                {asset.assetName}
                              </div>
                            </div>
                          </div>

                          <div className="flex flex-col items-start w-[35%]">
                            <div className="font-semibold">
                              {(() => {
                                const assetSymbol = asset.asset?.toUpperCase();
                                const decimals = assetSymbol === "BTC" ? 6 : 2;
                                return availableAmount
                                  .toLocaleString("en-US", {
                                    minimumFractionDigits: decimals,
                                    maximumFractionDigits: decimals,
                                  })
                                  .replace(/,/g, " ");
                              })()}
                            </div>
                            <div
                              className={`${fromDashboard ? "text-emerald-500" : "text-gray-500"}`}
                            >
                              {formattedBalance}
                            </div>
                          </div>

                          <div
                            className={`flex-col items-start w-[30%] ${fromDashboard ? "flex" : "hidden md:flex"}`}
                          >
                            <div className="font-semibold">
                              {asset.asset?.toLowerCase() === "zar" ||
                              asset.assetName === "South African Rand"
                                ? " "
                                : estimatedPrice
                                    .toLocaleString("en-US", {
                                      minimumFractionDigits: 2,
                                      maximumFractionDigits: 2,
                                    })
                                    .replace(/,/g, " ")}
                            </div>
                            {!fromDashboard && has24hChange && (
                              <div
                                className={
                                  isPositiveChange
                                    ? "text-emerald-500"
                                    : "text-red-500"
                                }
                              >
                                {formatted24hChange}
                              </div>
                            )}
                          </div>

                          {!fromDashboard && (
                            <>
                              <div className="md:flex hidden items-center justify-end gap-2 w-[14%] ">
                                <DropdownMenu>
                                  <DropdownMenuTrigger asChild>
                                    <Button
                                      variant="outline"
                                      size="icon"
                                      className="rounded-full z-[11] h-8 w-8"
                                      onClick={(e) => e.stopPropagation()}
                                    >
                                      <MoreVertical className="w-4 h-4 text-[#6D6584]" />
                                    </Button>
                                  </DropdownMenuTrigger>
                                  <DropdownMenuContent
                                    sideOffset={8}
                                    className="min-w-[11rem] rounded-lg p-3"
                                  >
                                    {featureFlags?.buyCrypto && (
                                      <DropdownMenuItem asChild>
                                        <button
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            handleTransaction(asset, "a");
                                          }}
                                          className="flex justify-between items-center w-full gap-3 sm:gap-14 px-3 py-3 sm:py-5 text-sm hover:bg-gray-100"
                                        >
                                          <span>Buy</span>
                                          <div className="flex justify-center items-center border bg-white h-8 w-8 sm:h-10 sm:w-10 rounded-full hover:bg-gray-100">
                                            <ArrowRight className="w-4 h-4 sm:w-5 sm:h-5 text-gray-500" />
                                          </div>
                                        </button>
                                      </DropdownMenuItem>
                                    )}
                                    {featureFlags?.sellCrypto && (
                                      <DropdownMenuItem asChild>
                                        <button
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            handleTransaction(asset, "b");
                                          }}
                                          className="flex justify-between items-center w-full gap-3 sm:gap-14 px-3 py-3 sm:py-5 text-sm hover:bg-gray-100"
                                        >
                                          <span>Sell</span>
                                          <div className="flex justify-center items-center border bg-white h-8 w-8 sm:h-10 sm:w-10 rounded-full hover:bg-gray-100">
                                            <ArrowRight className="w-4 h-4 sm:w-5 sm:h-5 text-gray-500" />
                                          </div>
                                        </button>
                                      </DropdownMenuItem>
                                    )}
                                    {featureFlags?.sendCrypto && (
                                      <DropdownMenuItem asChild>
                                        <button
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            handleTransaction(asset, "c");
                                          }}
                                          className="flex justify-between items-center w-full gap-3 sm:gap-14 px-3 py-3 sm:py-5 text-sm hover:bg-gray-100"
                                        >
                                          <span>Send</span>
                                          <div className="flex justify-center items-center border bg-white h-8 w-8 sm:h-10 sm:w-10 rounded-full hover:bg-gray-100">
                                            <ArrowRight className="w-4 h-4 sm:w-5 sm:h-5 text-gray-500" />
                                          </div>
                                        </button>
                                      </DropdownMenuItem>
                                    )}
                                    {featureFlags?.receiveCrypto && (
                                      <DropdownMenuItem asChild>
                                        <button
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            handleTransaction(asset, "d");
                                          }}
                                          className="flex justify-between items-center w-full gap-3 sm:gap-14 px-3 py-3 sm:py-5 text-sm hover:bg-gray-100"
                                        >
                                          <span>Receive</span>
                                          <div className="flex justify-center items-center border bg-white h-8 w-8 sm:h-10 sm:w-10 rounded-full hover:bg-gray-100">
                                            <ArrowRight className="w-4 h-4 sm:w-5 sm:h-5 text-gray-500" />
                                          </div>
                                        </button>
                                      </DropdownMenuItem>
                                    )}
                                  </DropdownMenuContent>
                                </DropdownMenu>
                                <button
                                  onClick={() =>
                                    handleOpenAssetDashboard(asset)
                                  }
                                  className="w-8 h-8 rounded-full border border-[#EBE9EF] flex items-center justify-center hover:bg-gray-50 transition-colors"
                                >
                                  <ArrowRight className="w-4 h-4 text-[#6D6584]" />
                                </button>
                              </div>
                              <div className="md:hidden flex justify-end items-center flex-1">
                                <button
                                  onClick={() =>
                                    handleOpenAssetDashboard(asset)
                                  }
                                  className="w-8 h-8 rounded-full border border-[#EBE9EF] flex items-center justify-center hover:bg-gray-50 transition-colors"
                                >
                                  <ArrowRight className="w-4 h-4 text-[#6D6584]" />
                                </button>
                              </div>
                            </>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </>
          ) : (
            <div className="flex-1 flex flex-col items-center justify-center text-center space-y-4 my-10">
              <div className="flex items-center justify-center mb-2">
                <img src={carouselItem} alt="" className="w-28 lg:w-[160px]" />
              </div>

              <h3 className="text-lg lg:text-2xl font-semibold text-brand-primary">
                Add funds, Buy crypto
              </h3>

              <p className="text-sm text-gray-600 leading-relaxed max-w-lg">
                Buy, sell, and manage your digital assets.
              </p>
              <button
                onClick={() => {
                  navigate("/trading/digitalWallet", {
                    state: {
                      type: "buy-sell",
                      selectedTab: "Buy",
                      tabActive: "Buy",
                    },
                  });
                  window.scrollTo(0, 0);
                }}
                className="h-[48px] flex items-center justify-center md:w-[150px] w-full bg-white border border-[#EBE9EF] text-[#6D6584] rounded-full text-[14px] font-semibold hover:bg-[#F5F4F7] transition-colors"
              >
                Buy Crypto
              </button>
            </div>
          )}
        </div>
        {assetsForUse?.length > 0 && fromDashboard && (
          <div className="border-t">
            <Button
              onClick={handleNavigate}
              variant="ghost"
              className="px-6 py-8 text-[#6D6584] w-full flex items-center justify-between group rounded-none hover:no-underline"
            >
              <span>View All Assets</span>
              <div className="border p-2 rounded-full bg-transparent transition-transform group-hover:translate-x-1">
                <ArrowRight className="h-4 w-4" />
              </div>
            </Button>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
